import type Phaser from 'phaser';
import type { IsoProjection } from '../core/iso';
import { objectDepth } from './depth';
import { ACTION_FIRST, WALK_FRAMES, animKey, animSheet } from './unitAnim';

/** Painted worker sprites (D136): public/art/units/worker-<kind>.webp. */
export const WORKER_KINDS = ['wood', 'food', 'scrap'] as const;
export type WorkerKind = (typeof WORKER_KINDS)[number];
export const workerKey = (k: WorkerKind): string => `paint-worker-${k}`;

/** What one staffed workplace looks like to the renderer. */
export interface Workplace {
  readonly id: number;
  readonly kind: WorkerKind;
  /** Footprint origin and size in tiles. */
  readonly x: number;
  readonly y: number;
  readonly w: number;
  readonly h: number;
  readonly workers: number;
}

const HEIGHT = 30; // world px, a little shorter than soldiers
const SPEED = 0.35; // tiles per second
const MAX_SHOWN = 2;

interface Walker {
  readonly img: Phaser.GameObjects.Image;
  readonly phase: number;
  /** D137: has a walk + work frame sheet. */
  readonly animated: boolean;
}

/**
 * Survivors at work (D136). Purely visual: each staffed workplace shows up to two
 * of its workers pacing along the building's front edge — the simulation still
 * only counts workers, so nothing here can affect the game.
 */
export class WorkerRenderer {
  private readonly crews = new Map<number, { place: Workplace; walkers: Walker[] }>();
  private t = 0;

  constructor(
    private readonly scene: Phaser.Scene,
    private readonly iso: IsoProjection,
  ) {}

  get count(): number {
    let n = 0;
    for (const c of this.crews.values()) n += c.walkers.length;
    return n;
  }

  /** World positions of the shown workers (debug / tests). */
  positions(): { x: number; y: number; visible: boolean; depth: number; key: string }[] {
    const out: { x: number; y: number; visible: boolean; depth: number; key: string }[] = [];
    for (const c of this.crews.values()) for (const w of c.walkers) out.push({ x: w.img.x, y: w.img.y, visible: w.img.visible, depth: w.img.depth, key: w.img.texture.key });
    return out;
  }

  /** Match the crews to the workplaces (cheap: only adds / removes what changed). */
  sync(places: readonly Workplace[]): void {
    const seen = new Set<number>();
    for (const p of places) {
      const want = Math.min(MAX_SHOWN, p.workers);
      if (want <= 0 || !this.scene.textures.exists(workerKey(p.kind))) continue;
      seen.add(p.id);
      let crew = this.crews.get(p.id);
      if (!crew) {
        crew = { place: p, walkers: [] };
        this.crews.set(p.id, crew);
      }
      crew.place = p;
      while (crew.walkers.length < want) {
        const sheet = animSheet(`worker-${p.kind}`);
        const animated = sheet !== null && this.scene.textures.exists(animKey(`worker-${p.kind}`));
        const img = animated
          ? this.scene.add.image(0, 0, animKey(`worker-${p.kind}`), 0).setOrigin(0.5, sheet.footY / sheet.fh).setScale(HEIGHT / sheet.bodyH)
          : this.scene.add.image(0, 0, workerKey(p.kind)).setOrigin(0.5, 0.95);
        if (!animated) img.setScale(HEIGHT / img.height);
        crew.walkers.push({ img, phase: crew.walkers.length * 0.5 + (p.id % 7) * 0.13, animated });
      }
      while (crew.walkers.length > want) crew.walkers.pop()!.img.destroy();
    }
    for (const [id, crew] of this.crews) {
      if (seen.has(id)) continue;
      for (const w of crew.walkers) w.img.destroy();
      this.crews.delete(id);
    }
  }

  update(dt: number): void {
    this.t += dt;
    for (const { place: p, walkers } of this.crews.values()) {
      // Pace along the front (south-east) edge, just outside the footprint, and back.
      const len = Math.max(1, p.w - 0.4);
      for (const w of walkers) {
        // A loop of: walk out (0..1), work for a moment, walk back (1..2), work again.
        const cyc = ((this.t * SPEED) / len + w.phase) % 2.6;
        let f: number;
        let working = false;
        if (cyc < 1) f = cyc;
        else if (cyc < 1.3) (f = 1), (working = true);
        else if (cyc < 2.3) f = 1 - (cyc - 1.3);
        else (f = 0), (working = true);
        const back = cyc >= 1.3 && cyc < 2.3;
        const gx = p.x + 0.2 + f * len - 0.5;
        const gy = p.y + p.h + 0.3 - 0.5;
        const c = this.iso.gridToWorld(gx, gy);
        if (w.animated) {
          const frame = working ? ACTION_FIRST + (Math.floor(this.t * 6 + w.phase * 10) % 4) : Math.floor(this.t * 7 + w.phase * 10) % WALK_FRAMES;
          w.img.setFrame(frame).setPosition(c.x, c.y);
        } else {
          const bob = working ? 0 : Math.abs(Math.sin(this.t * 7 + w.phase * 9)) * 1.4;
          w.img.setPosition(c.x, c.y - bob);
        }
        w.img
          .setFlipX(back)
          // Never behind their own building (its depth sorts by the front tile), still sorted against the rest.
          .setDepth(Math.max(objectDepth(this.iso, gx, gy, 5), objectDepth(this.iso, p.x + p.w - 1, p.y + p.h - 1, 60)));
      }
    }
  }

  destroy(): void {
    for (const crew of this.crews.values()) for (const w of crew.walkers) w.img.destroy();
    this.crews.clear();
  }
}
